import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { brl, fmtDate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import {
  Package, PaperPlaneTilt, MapPin, X, Check, ClipboardText, Truck, Clock, Prohibit, ArrowSquareOut, Image as ImageIcon,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  pending: { label: "Pendente", color: "bg-brand-yellow", icon: Clock },
  shipped: { label: "Enviado", color: "bg-brand-success text-white", icon: Truck },
  canceled: { label: "Cancelado", color: "bg-brand-peach", icon: Prohibit },
};

export default function Expedition() {
  const { user } = useAuth();
  const [data, setData] = useState({ customer: null, orders: [] });
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [av, mine] = await Promise.all([
        api.get("/expedition/available", { params: { _: Date.now() } }),
        api.get("/expedition/requests/mine", { params: { _: Date.now() } }),
      ]);
      setData(av.data);
      setRequests(mine.data);
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || "Erro ao carregar");
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    load();
    // Real-time: refresh silently when the tab regains focus/visibility and
    // poll every 20s while the tab is visible so status/tracking updates from
    // the admin land without a manual reload.
    const refreshSilent = () => load({ silent: true });
    const onFocus = () => { if (document.visibilityState === "visible") refreshSilent(); };
    const onVisibility = () => { if (document.visibilityState === "visible") refreshSilent(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refreshSilent();
    }, 20000);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, [load]);

  const toggle = (oid) => {
    const s = new Set(selected);
    if (s.has(oid)) s.delete(oid); else s.add(oid);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === data.orders.length) setSelected(new Set());
    else setSelected(new Set(data.orders.map(o => o.id)));
  };
  const total = useMemo(() =>
    data.orders.filter(o => selected.has(o.id))
      .reduce((s, o) => s + o.price * (o.quantity || 1), 0),
    [selected, data.orders]
  );

  const grouped = useMemo(() => {
    const map = new Map();
    for (const o of data.orders) {
      const key = o.live_id || "sem-live";
      if (!map.has(key)) map.set(key, { live_id: o.live_id, live_name: o.live_name, live_date: o.live_date, items: [] });
      map.get(key).items.push(o);
    }
    return Array.from(map.values());
  }, [data.orders]);

  return (
    <div data-testid="expedition-page" className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="label-brutal">Expedição</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black">Meus itens para envio</h1>
          <p className="text-brand-ink2 mt-2">
            Selecione os produtos que já pagou e solicite o envio para seu endereço.
            {data.customer && (
              <> Vinculado a <strong>{data.customer.handle}</strong> — {data.customer.full_name}.</>
            )}
          </p>
        </div>
        <Link
          to="/me/profile"
          data-testid="expedition-goto-my-profile"
          className="btn-white h-[52px] whitespace-nowrap"
        >
          Meu cadastro
        </Link>
      </header>

      {loading ? (
        <div className="card-brutal p-10 text-center font-bold">Carregando…</div>
      ) : data.orders.length === 0 ? (
        <div className="card-brutal p-10 text-center">
          <Package weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Nenhum item disponível para envio</p>
          <p className="text-brand-ink2 text-sm">Assim que houver produtos pagos vinculados à sua @arroba, eles aparecerão aqui.</p>
        </div>
      ) : (
        <div className="card-brutal overflow-hidden">
          <div className="bg-brand-pink text-white p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase">{selected.size} de {data.orders.length} selecionado(s)</p>
              <p className="font-heading text-xl font-black">Total selecionado: {brl(total)}</p>
            </div>
            <div className="flex gap-2">
              <button data-testid="select-all-btn" onClick={toggleAll} className="btn-white text-brand-ink">
                {selected.size === data.orders.length ? "Desmarcar todos" : "Marcar todos"}
              </button>
              <button
                data-testid="request-shipment-btn"
                disabled={selected.size === 0}
                onClick={() => setShowModal(true)}
                className="btn-yellow disabled:opacity-40"
              >
                <PaperPlaneTilt weight="bold" size={16} /> Solicitar envio ({selected.size})
              </button>
            </div>
          </div>
          <div className="divide-y-4 divide-brand-ink">
            {grouped.map(g => (
              <div key={g.live_id || "-"} className="p-3 bg-brand-cream">
                <div className="flex items-center gap-2 mb-2">
                  <span className="badge-brutal !bg-brand-mint">Live: {g.live_name || "—"}</span>
                  {g.live_date && <span className="badge-brutal !bg-white">{fmtDate(g.live_date)}</span>}
                </div>
                <table className="tbl w-full">
                  <thead>
                    <tr>
                      <th className="w-10"></th>
                      <th className="w-16">Foto</th>
                      <th className="w-24">Código</th>
                      <th>Descrição</th>
                      <th className="w-16 text-right">Qtd</th>
                      <th className="w-28 text-right">Preço</th>
                      <th className="w-28 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map(o => (
                      <tr key={o.id} data-testid={`available-item-${o.id}`}>
                        <td className="text-center">
                          <input
                            data-testid={`check-item-${o.id}`}
                            type="checkbox"
                            checked={selected.has(o.id)}
                            onChange={() => toggle(o.id)}
                            className="w-4 h-4 accent-brand-pink"
                          />
                        </td>
                        <td className="text-center">
                          {o.photo_url ? (
                            <button
                              type="button"
                              data-testid={`photo-btn-${o.id}`}
                              onClick={() => setLightbox(o.photo_url)}
                              className="w-12 h-12 border-2 border-brand-ink bg-white overflow-hidden inline-flex items-center justify-center hover:ring-2 hover:ring-brand-pink"
                              title="Ver foto"
                            >
                              <img src={o.photo_url} alt={o.description || "peça"} className="w-full h-full object-cover" />
                            </button>
                          ) : (
                            <span
                              className="w-12 h-12 border-2 border-dashed border-brand-ink2 inline-flex items-center justify-center text-brand-ink2"
                              title="Sem foto"
                            >
                              <ImageIcon weight="bold" size={16} />
                            </span>
                          )}
                        </td>
                        <td className="font-bold">{o.product_code || "—"}</td>
                        <td>{o.description || "—"}</td>
                        <td className="text-right">{o.quantity || 1}</td>
                        <td className="text-right">{brl(o.price)}</td>
                        <td className="text-right font-bold">{brl(o.price * (o.quantity || 1))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      <section>
        <h2 className="font-heading text-2xl font-black mb-3 flex items-center gap-2">
          <ClipboardText weight="bold" size={22} /> Envios em andamento
        </h2>
        {requests.length === 0 ? (
          <div className="card-brutal p-6 text-center text-brand-ink2">Nenhum envio em andamento.</div>
        ) : (
          <div className="card-brutal overflow-x-auto">
            <table className="tbl w-full">
              <thead>
                <tr>
                  <th className="w-32">Data</th>
                  <th className="w-24 text-right">Itens</th>
                  <th className="w-28 text-right">Total</th>
                  <th className="w-32">Status</th>
                  <th>Rastreio</th>
                  <th>Notas</th>
                </tr>
              </thead>
              <tbody>
                {requests.map(r => {
                  const S = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
                  return (
                    <tr key={r.id} data-testid={`my-request-${r.id}`}>
                      <td>{new Date(r.created_at).toLocaleString("pt-BR")}</td>
                      <td className="text-right">{r.items_count}</td>
                      <td className="text-right font-bold">{brl(r.total_amount)}</td>
                      <td>
                        <span className={`badge-brutal ${S.color}`}>
                          <S.icon weight="bold" size={12} /> {S.label}
                        </span>
                      </td>
                      <td>
                        {r.tracking_code
                          ? <TrackingLink code={r.tracking_code} carrier={r.carrier} testid={`tracking-${r.id}`} />
                          : <span className="text-brand-ink2 text-sm">—</span>}
                      </td>
                      <td className="text-brand-ink2 text-sm">{r.notes || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {showModal && (
        <RequestModal
          customer={data.customer}
          selectedOrders={data.orders.filter(o => selected.has(o.id))}
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); setSelected(new Set()); load(); }}
        />
      )}

      {lightbox && (
        <div
          data-testid="photo-lightbox"
          className="fixed inset-0 bg-brand-ink/80 z-[60] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="photo-lightbox-close"
              className="absolute -top-3 -right-3 bg-white border-2 border-brand-ink p-2 z-10"
            >
              <X weight="bold" size={16} />
            </button>
            <img
              src={lightbox}
              alt="Foto da peça"
              className="max-w-[92vw] max-h-[85vh] object-contain border-4 border-brand-ink bg-white"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function RequestModal({ customer, selectedOrders, onClose, onCreated }) {
  const [addr, setAddr] = useState({
    postal_code: customer?.cep || "",
    street: customer?.street || "",
    number: customer?.number || "",
    complement: customer?.complement || "",
    neighborhood: customer?.neighborhood || "",
    city: customer?.city || "",
    state: customer?.state || "",
    recipient_name: customer?.full_name || "",
    recipient_phone: customer?.whatsapp || customer?.phone_mobile || "",
  });
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const total = selectedOrders.reduce((s, o) => s + o.price * (o.quantity || 1), 0);
  const items = selectedOrders.reduce((s, o) => s + (o.quantity || 1), 0);

  const set = (k, v) => setAddr(a => ({ ...a, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!addr.recipient_name.trim() || !addr.postal_code.trim() || !addr.city.trim()) {
      return toast.error("Preencha nome, CEP e cidade");
    }
    setSaving(true);
    try {
      await api.post("/expedition/requests", {
        order_ids: selectedOrders.map(o => o.id),
        address: addr,
        notes: notes.trim() || null,
      });
      toast.success("Solicitação enviada!");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao criar solicitação");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-brand-ink/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div data-testid="request-modal" className="card-brutal w-full max-w-2xl bg-white p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <div>
            <p className="label-brutal">Nova solicitação</p>
            <h2 className="font-heading text-2xl font-black">Endereço de envio</h2>
          </div>
          <button onClick={onClose} className="p-2 border-2 border-brand-ink"><X weight="bold" size={16} /></button>
        </div>
        <div className="card-brutal p-3 bg-brand-yellow mb-4">
          <p className="text-sm"><strong>{items}</strong> item(ns) selecionado(s) · Total: <strong>{brl(total)}</strong></p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-brutal">Nome do destinatário *</label>
              <input data-testid="addr-recipient-input" className="input-brutal" value={addr.recipient_name} onChange={(e) => set("recipient_name", e.target.value)} />
            </div>
            <div>
              <label className="label-brutal">Telefone/WhatsApp</label>
              <input data-testid="addr-phone-input" className="input-brutal" value={addr.recipient_phone} onChange={(e) => set("recipient_phone", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label-brutal">CEP *</label>
              <input data-testid="addr-cep-input" className="input-brutal" value={addr.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <label className="label-brutal">Rua</label>
              <input data-testid="addr-street-input" className="input-brutal" value={addr.street} onChange={(e) => set("street", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label-brutal">Número</label>
              <input data-testid="addr-number-input" className="input-brutal" value={addr.number} onChange={(e) => set("number", e.target.value)} />
            </div>
            <div>
              <label className="label-brutal">Complemento</label>
              <input data-testid="addr-complement-input" className="input-brutal" value={addr.complement} onChange={(e) => set("complement", e.target.value)} />
            </div>
            <div>
              <label className="label-brutal">Bairro</label>
              <input data-testid="addr-neighborhood-input" className="input-brutal" value={addr.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-brutal">Cidade *</label>
              <input data-testid="addr-city-input" className="input-brutal" value={addr.city} onChange={(e) => set("city", e.target.value)} />
            </div>
            <div>
              <label className="label-brutal">Estado</label>
              <input data-testid="addr-state-input" className="input-brutal" value={addr.state} onChange={(e) => set("state", e.target.value)} />
            </div>
          </div>
          <div>
            <label className="label-brutal">Observações</label>
            <textarea data-testid="notes-input" rows={2} className="input-brutal" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex.: entregar após 14h" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-white">Cancelar</button>
            <button data-testid="submit-request-btn" type="submit" disabled={saving} className="btn-pink">
              <Check weight="bold" size={16} /> {saving ? "Enviando…" : "Confirmar solicitação"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// Builds the best tracking URL for a given carrier and code.
function buildTrackingUrl(carrier, code) {
  const c = (carrier || "correios").toLowerCase();
  const clean = (code || "").trim().toUpperCase().replace(/\s+/g, "");
  if (!clean) return null;
  if (c.includes("jadlog")) return `https://www.jadlog.com.br/tracking?cte=${encodeURIComponent(clean)}`;
  if (c.includes("loggi")) return `https://www.loggi.com/rastreador/?tracking=${encodeURIComponent(clean)}`;
  if (c.includes("total")) return `https://www.totalexpress.com.br/rastreamento/?codigo=${encodeURIComponent(clean)}`;
  // Correios (default): official site accepts the object code as query.
  return `https://rastreamento.correios.com.br/app/index.php?objeto=${encodeURIComponent(clean)}`;
}

function TrackingLink({ code, carrier, testid }) {
  const url = buildTrackingUrl(carrier, code);
  const copy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Código copiado");
    } catch { toast.error("Falha ao copiar"); }
  };
  return (
    <div data-testid={testid} className="flex items-center gap-2 flex-wrap">
      <span className="badge-brutal !bg-brand-mint">{carrier || "Correios"}</span>
      <button
        type="button"
        onClick={copy}
        data-testid={`${testid}-copy`}
        title="Copiar código"
        className="font-mono text-sm px-1.5 py-0.5 border-2 border-brand-ink bg-white hover:bg-brand-yellow"
      >
        {code}
      </button>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`${testid}-link`}
          className="inline-flex items-center gap-1 text-xs font-bold text-brand-pink hover:underline"
        >
          <ArrowSquareOut weight="bold" size={12} /> Rastrear
        </a>
      )}
    </div>
  );
}
